import HttpService from '@/common/services/HttpService'
import {
  type CallDTOV1,
  type CallInteractionDTOV2,
  type CallRequestBody,
  type InboundCallConfigurationDTOV2,
  type OutboundCallConfigurationDTOV2,
  type PatchCallInteractionDTOV2,
  type ProviderGeneralConfigurationDTOV2,
  type VoiceConfigurationDTOV1,
  type VoiceConfigurationDTOV2,
  type AgentModel,
  VoiceProvider,
  type CallParams,
  CustomerPhoneFieldEnum,
  ProviderConnectionType,
} from '@/common/types/cti'
import { displayMessage } from '@/common/services/NotificationDisplayService'
import i18n from '@/plugins/i18n'
import router from '@/router'

const { t } = i18n.global

const configurationPath = `call/api/v1/configuration/`
const defaultConfigurationPath = `call/api/v2/default-configuration/`
const generalConfigurationsPath = `/call/api/v2/configurations`
const inboundCallConfigurationPath = `/call/api/v2/inbound-call-configurations`
const outboundCallConfigurationPath = `/call/api/v2/outbound-call-configurations`

const connectUrl = `call/api/v2/configurations`

const agentDataPath = '/call/api/v2/agents/current'

export const getVoiceProvider = async () => {
  try {
    return (await HttpService.getData<VoiceConfigurationDTOV1>(configurationPath))?.activeProvider ?? VoiceProvider.NONE
  } catch (error) {
    console.error(error)
    return VoiceProvider.NONE
  }
}

export const getDefaultVoiceProvider = async () => {
  try {
    return (await HttpService.getData<VoiceConfigurationDTOV2>(defaultConfigurationPath))?.defaultProvider ?? VoiceProvider.NONE
  } catch (error) {
    console.error(error)
    return VoiceProvider.NONE
  }
}

export const putDefaultVoiceConfiguration = async (defaultProvider: VoiceProvider) => {
  try {
    const res = await HttpService.putData<VoiceConfigurationDTOV2>(defaultConfigurationPath, { defaultProvider })
    displayMessage(t('assist.admin.voice.provider.message.updated'), 'success')
    return res
  } catch (error) {
    console.error(error)
    displayMessage(t('assist.admin.voice.provider.message.update.error', { provider: defaultProvider }), 'danger')
    return
  }
}

export const generateToken = async (configurationId: UUID | null) => {
  try {
    return await HttpService.putData(`${connectUrl}/${configurationId}/connect`)
  } catch (error) {
    console.error(error)
    displayMessage(t('assist.admin.voice.axialys.token.error'), 'danger')
    throw error
  }
}

export const getAllProvidersConfigurations = async (): Promise<ProviderGeneralConfigurationDTOV2[]> => {
  try {
    return await HttpService.getData<ProviderGeneralConfigurationDTOV2[]>(`${generalConfigurationsPath}`)
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const createProviderConfiguration = async (configuration: ProviderGeneralConfigurationDTOV2): Promise<ProviderGeneralConfigurationDTOV2> => {
  try {
    const res = await HttpService.postData<ProviderGeneralConfigurationDTOV2>(`${generalConfigurationsPath}`, configuration)
    displayMessage(t('assist.admin.voice.provider.create.success'), 'success')
    return res
  } catch (error) {
    console.error(error)
    displayMessage(t('assist.admin.voice.provider.create.error'), 'danger')
    throw error
  }
}

export const updateProviderConfiguration = async (
  configuration: ProviderGeneralConfigurationDTOV2,
  showSuccessMessage: boolean = true,
): Promise<ProviderGeneralConfigurationDTOV2> => {
  try {
    const res = await HttpService.putData<ProviderGeneralConfigurationDTOV2>(`${generalConfigurationsPath}/${configuration.id}`, configuration)
    if (showSuccessMessage) {
      displayMessage(t('assist.admin.voice.provider.update.success'), 'success')
    }
    return res
  } catch (error) {
    console.error(error)
    displayMessage(t('assist.admin.voice.provider.update.error'), 'danger')
    throw error
  }
}

export const setDefaultCustomerSettingsForAllConfigurations = async (): Promise<void> => {
  try {
    const providerConfigurations = await getAllProvidersConfigurations()
    const updatePromises = providerConfigurations.map(async (config: ProviderGeneralConfigurationDTOV2) => {
      if (config.id) {
        const updatedConfig: ProviderGeneralConfigurationDTOV2 = {
          ...config,
          iframeUrl: config.iframeUrl ?? '',
          connectionType: config.connectionType ?? ProviderConnectionType.MANUAL,
          creationCustomerPhoneFieldName: CustomerPhoneFieldEnum.MOBILEPHONE,
          creationCustomerWhenNotFoundEnabled: false,
          attachLatestCustomerWhenMultipleFoundEnabled: false,
        }
        await updateProviderConfiguration(updatedConfig, false)
      }
    })
    await Promise.all(updatePromises)
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const getAllInboundCallConfigurations = async (): Promise<InboundCallConfigurationDTOV2[]> => {
  try {
    return await HttpService.getData<InboundCallConfigurationDTOV2[]>(`${inboundCallConfigurationPath}`)
  } catch (error) {
    console.error(error)
    return []
  }
}

export const getInboundCallConfigurationById = async (id: UUID | string): Promise<InboundCallConfigurationDTOV2 | undefined> => {
  try {
    return await HttpService.getData<InboundCallConfigurationDTOV2>(`${inboundCallConfigurationPath}/${id}`)
  } catch (error) {
    console.error(error)
    return
  }
}

export const createInboundCallConfiguration = async (
  inboundCallConfiguration: InboundCallConfigurationDTOV2,
): Promise<InboundCallConfigurationDTOV2 | undefined> => {
  try {
    const res = await HttpService.postData<InboundCallConfigurationDTOV2>(inboundCallConfigurationPath, inboundCallConfiguration)
    displayMessage(t('assist.admin.voice.inbound.created'), 'success')
    return res
  } catch (error) {
    console.error(error)
    displayMessage(t('assist.admin.voice.inbound.create.error'), 'danger')

    return
  }
}

export const updateInboundCallConfiguration = async (
  inboundCallConfiguration: InboundCallConfigurationDTOV2,
): Promise<InboundCallConfigurationDTOV2 | undefined> => {
  try {
    const res = await HttpService.putData<InboundCallConfigurationDTOV2>(
      `${inboundCallConfigurationPath}/${inboundCallConfiguration.id}`,
      inboundCallConfiguration,
    )
    displayMessage(t('assist.admin.voice.inbound.updated'), 'success')
    return res
  } catch (error) {
    console.error(error)
    displayMessage(t('assist.admin.voice.inbound.update.error'), 'danger')
    return
  }
}

export const saveInboundCallConfigurationFactory =
  (screenCode: string, id: UUID | undefined) =>
  async ({ fields }: { fields: InboundCallConfigurationDTOV2 }) => {
    if (id) {
      await updateInboundCallConfiguration(fields)
    } else {
      await createInboundCallConfiguration(fields)
    }
    router.push({ name: screenCode })
  }

export const deleteInboundCallConfiguration = async (id: UUID) => {
  try {
    const res = await HttpService.delete(`${inboundCallConfigurationPath}/${id}`)
    displayMessage(t('assist.admin.voice.inbound.deleted'), 'success')
    return res
  } catch (error) {
    console.error(error)
    displayMessage(t('assist.admin.voice.inbound.delete.error'), 'danger')
    return false
  }
}

export const getAllOutboundCallConfigurations = async (): Promise<OutboundCallConfigurationDTOV2[]> => {
  try {
    return await HttpService.getData<OutboundCallConfigurationDTOV2[]>(`${outboundCallConfigurationPath}`)
  } catch (error) {
    console.error(error)
    return []
  }
}

export const getOutboundCallConfigurationById = async (id: UUID | string): Promise<OutboundCallConfigurationDTOV2 | undefined> => {
  try {
    return await HttpService.getData<OutboundCallConfigurationDTOV2>(`${outboundCallConfigurationPath}/${id}`)
  } catch (error) {
    console.error(error)
    return
  }
}

export const createOutboundCallConfiguration = async (
  outboundCallConfiguration: OutboundCallConfigurationDTOV2,
): Promise<OutboundCallConfigurationDTOV2 | undefined> => {
  try {
    const res = await HttpService.postData<OutboundCallConfigurationDTOV2>(outboundCallConfigurationPath, outboundCallConfiguration)
    displayMessage(t('assist.admin.voice.outbound.created'), 'success')
    return res
  } catch (error) {
    console.error(error)
    displayMessage(t('assist.admin.voice.outbound.create.error'), 'danger')

    return
  }
}

export const updateOutboundCallConfiguration = async (
  outboundCallConfiguration: OutboundCallConfigurationDTOV2,
): Promise<OutboundCallConfigurationDTOV2 | undefined> => {
  try {
    const res = await HttpService.putData<OutboundCallConfigurationDTOV2>(
      `${outboundCallConfigurationPath}/${outboundCallConfiguration.id}`,
      outboundCallConfiguration,
    )
    displayMessage(t('assist.admin.voice.outbound.updated'), 'success')
    return res
  } catch (error) {
    console.error(error)
    displayMessage(t('assist.admin.voice.outbound.update.error'), 'danger')

    return
  }
}

export const deleteOutboundCallConfiguration = async (id: UUID) => {
  try {
    const res = await HttpService.delete(`${outboundCallConfigurationPath}/${id}`)
    displayMessage(t('assist.admin.voice.outbound.deleted'), 'success')
    return res
  } catch (error) {
    console.error(error)
    displayMessage(t('assist.admin.voice.outbound.delete.error'), 'danger')
    return false
  }
}

export const saveOutboundCallConfigurationFactory =
  (screenCode: string, id: UUID | undefined) =>
  async ({ fields }: { fields: OutboundCallConfigurationDTOV2 }) => {
    if (id) {
      await updateOutboundCallConfiguration(fields)
    } else {
      await createOutboundCallConfiguration(fields)
    }
    router.push({ name: screenCode })
  }

export const httpNewCall = async (callRequestBody: CallRequestBody): Promise<CallDTOV1 | undefined> => {
  try {
    return await HttpService.postData<CallDTOV1>(`/call/api/v1/calls/`, callRequestBody)
  } catch (error) {
    console.error(error)
    displayMessage(t('assist.admin.voice.newcall.error'), 'danger')
  }
}

export const httpNewV2Call = async (callRequestBody: CallInteractionDTOV2): Promise<CallInteractionDTOV2 | undefined> => {
  try {
    return await HttpService.postData<CallInteractionDTOV2>(`/call/api/v2/calls/`, callRequestBody)
  } catch (error) {
    console.error(error)
    displayMessage(t('assist.admin.voice.newcall.error'), 'danger')
  }
}

export const httpV2PatchCall = async (
  providerConfigurationId: UUID | null,
  callId: string | null,
  callPatch: PatchCallInteractionDTOV2,
): Promise<PatchCallInteractionDTOV2> => {
  return await HttpService.patchData<PatchCallInteractionDTOV2>(`/call/api/v2/calls/${providerConfigurationId}/${callId}`, callPatch)
}

export const sendScreenPopEvent = async (callParams: CallParams, parkPageToken: string): Promise<void> => {
  try {
    return await HttpService.postData<void>(`/cti/ctiScreenPopEvent?parkPageToken=${parkPageToken}`, callParams)
  } catch (error) {
    console.error(error)
    displayMessage(t('assist.admin.cti.screepop.error'), 'danger')
  }
}

export const getAgentLogin = async (): Promise<AgentModel | null> => {
  try {
    return await HttpService.getData<AgentModel>(`${agentDataPath}`)
  } catch (error) {
    console.error(error)
    return null
  }
}

export const getAxialysConfigurationPasswordHint = async (id: UUID): Promise<string | null> => {
  try {
    return await HttpService.getData<string>(`${generalConfigurationsPath}/${id}/password`)
  } catch (error) {
    console.error(error)
    return null
  }
}

export const updateAxialysConfigurationPassword = async (id: UUID, password: string) => {
  try {
    const res = await HttpService.putData<string>(`${generalConfigurationsPath}/${id}/password`, password, {
      headers: {
        'Content-Type': 'text/plain',
      },
    })
    return res
  } catch (error) {
    console.error(error)
    return null
  }
}
